import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { TargetService } from '@app/target/services/target.service';
import { ContextService } from '@app/shared/services/context.service';
import { Injectable } from '@angular/core';
import { Target, ResourceResult } from '@app/target/target-api.model';
import { ResourceType, TargetType } from '@app/shared/models/core-api.model';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { TargetsService } from '@app/shared/services/targets.service';
import { SharedTargetService } from '@app/target/services/shared-target.service';
import { DisplayTypePipe } from '@app/shared/pipes/display-type.pipe';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class TargetResourceModalService {
  public i18n = this.translate.instant([
    'TargetResourcesForm_InputHelp',
    'TargetResourcesForm_Suggestions',
    'TargetResourcesForm_Selected',
    'Core_Remove',
    'Core_Saving',
    'TargetResourcesForm_NoResults',
    'TargetResourcesForm_NoResultsFound',
    'TargetResourcesForm_Title',
    'TargetResourcesForm_SearchPlaceHolder',
  ]);

  constructor(
    private contextService: ContextService,
    private displayTypePipe: DisplayTypePipe,
    private sharedTargetService: SharedTargetService,
    private targetService: TargetService,
    private targetsService: TargetsService,
    private trackerService: TrackerService,
    private translate: TranslateService,
    private webEnvironmentService: WebEnvironmentService
  ) {}

  /**
   * Apply for an opportunity request
   *
   * @param target Target object
   * @param showRole Boolean based on authUser.canManageTargets to show Page or Role/Plan modal
   */
  public createAPlan(target?: Target, showRole: boolean = false) {
    // Fallback case for when flex-row is used outside of a target
    // ie. on opportunities view.
    const orgId = target?.organizationId || -1;
    const targetInfo = {
      organizationId: showRole ? orgId : null,
      targetType: showRole ? null : ('Target' as TargetType),
    };

    return this.targetService
      .showTargetModal({
        target: targetInfo,
        context: showRole ? this.contextService.getContext() : null,
        isChild: true,
        mode: 'new',
        isAddToTarget: true,
      })
      .pipe(
        map(({ target }) => {
          return {
            referenceId: target.referenceId,
            image: showRole
              ? this.webEnvironmentService.getBlobUrl(target.image)
              : null,
            title: target.name,
            details: showRole ? target.subtitle : null,
            referenceType: 'Target',
          } as ResourceResult;
        })
      );
  }

  public getSuggestions(
    searchParams,
    endorsedSrc: string,
    init: boolean,
    target: Target,
    searchTerm?: string
  ): Observable<ResourceResult[]> {
    return this.targetsService.searchTargetResources(searchParams).pipe(
      map((response) => {
        const trackEventsData = [];
        const targetTypeForEvent =
          this.sharedTargetService.getTargetTypeForEvents(target?.targetType);

        response.forEach((result: ResourceResult) => {
          if (result.details) {
            switch (result.referenceType) {
              case 'User':
              case 'Pathway':
                break;
              default:
                result.details = this.displayTypePipe.transform(result.details);
            }
          }
          result.referenceId = result.referenceId || result.title;
          const properties = { ...result, ...target };

          const trackEvent = {
            eventName: targetTypeForEvent + ' Item Suggested',
            properties,
          };
          trackEventsData.push(trackEvent);
        });

        // Only do it once for getting a list of initial suggestions with no search term, no need to do it after the init
        if (trackEventsData.length > 0 && (init || searchTerm)) {
          this.trackerService.trackBatchEvents(trackEventsData);
        }

        // add the endorsed img if it's a tag and IsEndorsed
        const suggestions = response.map((item) => ({
          ...item,
          endorsedSrc:
            item.ReferenceType === 'Tag' && item.IsEndorsed ? endorsedSrc : '',
        }));

        return suggestions;
      }),
      catchError(() => of([]))
    );
  }

  public initTranslations(resourceType: ResourceType) {
    const typeDisplayNamePlural = this.translate.instant(
      this.targetsService.composeTargetResourceStringKey({
        baseKey: 'TargetResources_',
        primaryResourceType: resourceType,
        plural: true,
      })
    );

    return {
      targetResourcesForm_Title: this.translate.instant(
        'TargetResourcesForm_Title',
        { targetResourceType: typeDisplayNamePlural }
      ),
      targetResourcesForm_SearchPlaceHolder: this.translate.instant(
        'TargetResourcesForm_SearchPlaceHolder',
        { targetResourceType: typeDisplayNamePlural.toLowerCase() }
      ),
    };
  }
}
